import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const SESSION_MESSAGES = {
  idle:    'Your session expired due to inactivity. Please sign in again.',
  expired: 'Your session has expired. Please sign in again.',
};

const MODULES = [
  { img: '/login-1.png.png', label: 'HR Management',     sub: 'Employees · Org · Payroll' },
  { img: '/login-2.png.png', label: 'Leave & Attendance', sub: 'Leaves · WFH · Timesheets' },
  { img: '/login-3.png.png', label: 'Finance & Claims',   sub: 'Claims · Reports · Payslips' },
  { img: '/login-4.png.png', label: 'Projects & IT',      sub: 'Tasks · Assets · Tickets' },
];

function EyeIcon({ open }) {
  return open ? (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
      <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  ) : (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function Login() {
  const [prefix, setPrefix]             = useState('BT');
  const [numericId, setNumericId]       = useState('');
  const [password, setPassword]         = useState('');
  const [error, setError]               = useState('');
  const [sessionMsg, setSessionMsg]     = useState('');
  const [loading, setLoading]           = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [focused, setFocused]           = useState(null);
  const { login } = useAuth();
  const navigate  = useNavigate();

  const username = prefix + numericId;

  useEffect(() => {
    // If a password reset link was clicked, redirect to the reset page
    const params = new URLSearchParams(window.location.search);
    const resetToken = params.get('reset_token');
    if (resetToken) {
      navigate(`/reset-password?token=${resetToken}`, { replace: true });
      return;
    }
    const reason = sessionStorage.getItem('logout_reason');
    if (reason && SESSION_MESSAGES[reason]) {
      setSessionMsg(SESSION_MESSAGES[reason]);
      sessionStorage.removeItem('logout_reason');
    }
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!numericId.trim() || !password.trim()) { setError('Please enter your employee number and password.'); return; }
    if (!/^\d+$/.test(numericId)) { setError('Employee number must contain digits only.'); return; }
    setError('');
    setLoading(true);
    try {
      const userData = await login(username.trim(), password);
      if (userData?.must_change_password) {
        navigate('/change-password', { replace: true });
      } else {
        navigate('/employee/dashboard');
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Invalid credentials. Please try again.';
      setError(typeof msg === 'string' ? msg : 'Login failed. Please try again.');
    } finally { setLoading(false); }
  };

  return (
    <div style={{
      height: '100vh', width: '100vw', display: 'flex', overflow: 'hidden',
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
    }}>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

      {/* ── LEFT PANEL ── */}
      <div style={{
        flex: 1,
        background: '#1C1C1C',
        display: 'flex',
        flexDirection: 'column',
        padding: '2.5rem',
        overflow: 'hidden',
      }}>

        {/* Top row: Suite branding with logo — pinned to right */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-end', marginBottom: '1.75rem' }}>
          <div style={{ textAlign: 'right', display: 'flex', alignItems: 'center', gap: '0.625rem' }}>
            <img
              src="/bonton-suite-logo.png"
              alt="Bonton Suite"
              style={{ height: '36px', objectFit: 'contain' }}
              onError={(e) => { e.target.style.display = 'none'; }}
            />
            <div>
              <div style={{ lineHeight: 1 }}>
                <span style={{ fontSize: '2rem', fontWeight: 800, color: '#FFFFFF', letterSpacing: '-0.5px' }}>Bonton </span>
                <span style={{ fontSize: '2rem', fontWeight: 800, color: '#F97316', letterSpacing: '-0.5px' }}>Suite</span>
              </div>
              <p style={{ color: '#4B5563', fontSize: '0.72rem', margin: '0.4rem 0 0', textAlign: 'right' }}>
                HR · Projects · IT · Finance · Attendance
              </p>
            </div>
          </div>
        </div>

        {/* Headline */}
        <h2 style={{ fontSize: '1.2rem', fontWeight: 600, color: '#D1D5DB', margin: '0 0 0.4rem', lineHeight: 1.4 }}>
          One platform for <span style={{ color: '#F97316' }}>your entire organisation.</span>
        </h2>
        <p style={{ color: '#4B5563', fontSize: '0.78rem', margin: '0 0 1.5rem', lineHeight: 1.6 }}>
          Manage everything from one unified workspace.
        </p>

        {/* 2×2 grid — fixed row height, no stretching */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gridTemplateRows: '1fr 1fr',
          gap: '0.875rem',
          flex: 1,
          minHeight: 0,
        }}>
          {MODULES.map((m, i) => (
            <div key={i} style={{
              background: '#252525',
              borderRadius: '0.75rem',
              border: '1px solid rgba(255,255,255,0.07)',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              minHeight: 0,
            }}>
              <div style={{
                flex: 1,
                minHeight: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '0.75rem 0.75rem 0.4rem',
              }}>
                <img
                  src={m.img} alt={m.label}
                  style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              </div>
              <div style={{
                flexShrink: 0,
                padding: '0.4rem 0.875rem 0.6rem',
                borderTop: '1px solid rgba(255,255,255,0.05)',
              }}>
                <div style={{ fontSize: '0.76rem', fontWeight: 600, color: '#E5E7EB' }}>{m.label}</div>
                <div style={{ fontSize: '0.63rem', color: '#6B7280', marginTop: '0.1rem' }}>{m.sub}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <p style={{ fontSize: '0.65rem', color: '#3F3F3F', margin: '1.25rem 0 0' }}>
          © 2026 BonTon Softwares Pvt Ltd · Win in Style
        </p>
      </div>

      {/* ── RIGHT PANEL ── */}
      <div style={{
        width: '420px',
        flexShrink: 0,
        background: '#FFFFFF',
        borderLeft: '1px solid #E5E7EB',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '2.5rem',
        position: 'relative',
      }}>

        {/* Orange top bar */}
        <div style={{
          position: 'absolute', top: 0, left: 0, right: 0,
          height: '3px', background: '#F97316',
        }} />

        <div style={{ width: '100%' }}>

          {/* Header — centered */}
          <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
            <img
              src="/btlogo.png"
              alt="BonTon Softwares"
              style={{ height: '64px', objectFit: 'contain', marginBottom: '1.25rem' }}
              onError={(e) => { e.target.style.display = 'none'; }}
            />
            <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#111827', margin: '0 0 0.3rem' }}>
              Welcome back
            </h1>
            <p style={{ color: '#6B7280', fontSize: '0.875rem', margin: 0 }}>
              Sign in to <span style={{ color: '#F97316', fontWeight: 600 }}>Bonton Suite</span>
            </p>
          </div>

          {/* Alerts */}
          {sessionMsg && (
            <div style={{
              background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: '0.5rem',
              padding: '0.75rem 1rem', marginBottom: '1.25rem',
              color: '#92400E', fontSize: '0.82rem', lineHeight: 1.5,
            }}>
              {sessionMsg}
            </div>
          )}
          {error && (
            <div style={{
              background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '0.5rem',
              padding: '0.75rem 1rem', marginBottom: '1.25rem',
              color: '#DC2626', fontSize: '0.82rem', lineHeight: 1.5,
            }}>
              {error}
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 500, color: '#374151', marginBottom: '0.4rem' }}>
                Employee ID
              </label>
              <div style={{
                display: 'flex',
                border: `1.5px solid ${focused === 'user' ? '#F97316' : '#E5E7EB'}`,
                borderRadius: '0.5rem',
                overflow: 'hidden',
                boxShadow: focused === 'user' ? '0 0 0 3px rgba(249,115,22,0.1)' : 'none',
                transition: 'border-color 0.15s, box-shadow 0.15s',
              }}>
                <select
                  value={prefix}
                  onChange={e => setPrefix(e.target.value)}
                  onFocus={() => setFocused('user')}
                  onBlur={() => setFocused(null)}
                  style={{
                    padding: '0.72rem 0.5rem 0.72rem 0.75rem',
                    border: 'none', borderRight: '1.5px solid #E5E7EB',
                    background: '#F9FAFB', fontSize: '0.9rem', fontWeight: 600,
                    color: '#F97316', outline: 'none', cursor: 'pointer', flexShrink: 0,
                  }}
                >
                  <option value="BT">BT</option>
                  <option value="BTC">BTC</option>
                </select>
                <input
                  type="text"
                  inputMode="numeric"
                  value={numericId}
                  onChange={e => setNumericId(e.target.value.replace(/\D/g, ''))}
                  placeholder="e.g. 042"
                  autoComplete="username"
                  autoFocus
                  onFocus={() => setFocused('user')}
                  onBlur={() => setFocused(null)}
                  style={{
                    flex: 1, padding: '0.72rem 1rem',
                    border: 'none', fontSize: '0.9rem', color: '#111827',
                    background: '#FFFFFF', outline: 'none',
                  }}
                />
              </div>
              <p style={{ fontSize: '0.72rem', color: '#9CA3AF', margin: '0.3rem 0 0' }}>
                Select BT for Full-Time · BTC for Contract · Enter your numeric ID
              </p>
            </div>

            <div style={{ marginBottom: '1.75rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.4rem' }}>
                <label style={{ fontSize: '0.82rem', fontWeight: 500, color: '#374151' }}>
                  Password
                </label>
                <Link to="/forgot-password" style={{ fontSize: '0.78rem', color: '#F97316', fontWeight: 500, textDecoration: 'none' }}>
                  Forgot password?
                </Link>
              </div>
              <div style={{ position: 'relative' }}>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  autoComplete="current-password"
                  onFocus={() => setFocused('pass')}
                  onBlur={() => setFocused(null)}
                  style={{
                    width: '100%', boxSizing: 'border-box',
                    padding: '0.72rem 2.75rem 0.72rem 1rem',
                    border: `1.5px solid ${focused === 'pass' ? '#F97316' : '#E5E7EB'}`,
                    borderRadius: '0.5rem', fontSize: '0.9rem', color: '#111827',
                    background: '#FFFFFF', outline: 'none', transition: 'border-color 0.15s',
                    boxShadow: focused === 'pass' ? '0 0 0 3px rgba(249,115,22,0.1)' : 'none',
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{
                    position: 'absolute', right: '0.875rem', top: '50%', transform: 'translateY(-50%)',
                    background: 'none', border: 'none', cursor: 'pointer',
                    color: '#9CA3AF', display: 'flex', alignItems: 'center', padding: 0,
                  }}
                >
                  <EyeIcon open={showPassword} />
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%', padding: '0.8rem',
                background: loading ? '#FDBA74' : '#F97316',
                color: '#FFFFFF', border: 'none', borderRadius: '0.5rem',
                fontSize: '0.9rem', fontWeight: 600,
                cursor: loading ? 'not-allowed' : 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem',
                transition: 'background 0.15s',
              }}
              onMouseEnter={(e) => { if (!loading) e.currentTarget.style.background = '#EA580C'; }}
              onMouseLeave={(e) => { if (!loading) e.currentTarget.style.background = '#F97316'; }}
            >
              {loading ? (
                <>
                  <span style={{
                    width: '16px', height: '16px', flexShrink: 0,
                    border: '2px solid rgba(255,255,255,0.4)',
                    borderTop: '2px solid white', borderRadius: '50%',
                    animation: 'spin 0.75s linear infinite', display: 'inline-block',
                  }} />
                  Signing in...
                </>
              ) : 'Sign In'}
            </button>
          </form>

          <p style={{ textAlign: 'center', marginTop: '1.75rem', marginBottom: 0, fontSize: '0.72rem', color: '#9CA3AF' }}>
            © 2026 BonTon Softwares Pvt Ltd. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
}

export default Login;
